/**
 * MediaPicker — the ONE owner-facing image control for Website Management.
 *
 * Sprint: TREATX-WEBSITE-MGMT-01 (Stage 1)
 *
 * SSOT: talks ONLY to the existing canonical CMS media endpoints served by
 * `cms_media_routes` + `media_storage_service` (AWS S3):
 *   POST   /api/admin/media/direct-upload   — upload from the owner's device
 *   GET    /api/admin/media/library         — pick an already-uploaded asset
 *
 * NO new storage provider, NO new media collection, NO second asset authority.
 * Historical Emergent Object Storage assets are NOT migrated or referenced.
 *
 * Existing URL-based records stay valid: whatever string is already in the
 * field is previewed and kept. Typing a URL by hand is still POSSIBLE (under
 * "Use a link instead") but is no longer the required workflow.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  ImageIcon, Loader2, Trash2, Upload, Library, Link2, X, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

const ALLOWED = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024;

const LibraryModal = ({ open, onClose, onSelect, testId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/media/library?limit=60');
      setItems((data?.items || []).filter((i) => i.public_url));
    } catch {
      toast.error('Could not load the image library');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      data-testid={`${testId}-library-modal`}
    >
      <div className="max-h-[80vh] w-full max-w-3xl overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <Library className="h-4 w-4 text-slate-500" />
            <h3 className="text-sm font-semibold text-slate-900">Choose an uploaded image</h3>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={load} disabled={loading}
                    data-testid={`${testId}-library-refresh`}>
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={onClose}
                    data-testid={`${testId}-library-close`}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="max-h-[62vh] overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <p className="py-16 text-center text-sm text-slate-500"
               data-testid={`${testId}-library-empty`}>
              No images uploaded yet. Use <strong>Upload</strong> to add the first one.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {items.map((item) => (
                <button
                  key={item.file_key}
                  type="button"
                  onClick={() => { onSelect(item.public_url); onClose(); }}
                  className="group overflow-hidden rounded-lg border border-slate-200 text-left transition-all hover:border-sky-400 hover:shadow-md"
                  data-testid={`${testId}-library-item`}
                >
                  <img
                    src={item.public_url}
                    alt={item.file_name || 'Uploaded image'}
                    loading="lazy"
                    className="h-24 w-full bg-slate-100 object-cover transition-transform group-hover:scale-[1.03]"
                  />
                  <p className="truncate px-2 py-1.5 text-[11px] text-slate-600">
                    {item.file_name || item.file_key}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const MediaLibraryModal = LibraryModal;

export const MediaPicker = ({
  value,
  onChange,
  label,
  hint,
  altText,
  onAltTextChange,
  caption,
  onCaptionChange,
  testId = 'media',
}) => {
  const [uploading, setUploading] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!ALLOWED.includes(file.type)) {
      toast.error('Only JPG, PNG and WebP images can be uploaded');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('That image is larger than 5 MB. Please choose a smaller file.');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/admin/media/direct-upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data?.success && data?.public_url) {
        onChange(data.public_url);
        toast.success('Image uploaded');
      } else {
        toast.error(data?.error || 'The image could not be uploaded');
      }
    } catch (e) {
      // Surface the real reason rather than a generic failure.
      const detail = e?.response?.data?.detail || e?.response?.data?.error;
      toast.error(detail || e.message || 'The image could not be uploaded');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div data-testid={`${testId}-picker`}>
      {label && <Label className="text-xs">{label}</Label>}

      <div
        className={`mt-1 rounded-lg border border-dashed p-3 transition-colors ${
          dragOver ? 'border-sky-400 bg-sky-50' : 'border-slate-300 bg-slate-50/60'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files?.[0]); }}
      >
        <input
          ref={fileRef}
          type="file"
          accept=".jpg,.jpeg,.png,.webp"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid={`${testId}-file-input`}
        />

        <div className="flex items-start gap-3">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-white">
            {value ? (
              <img
                src={value}
                alt={altText || 'Selected image'}
                className="h-full w-full object-cover"
                data-testid={`${testId}-preview`}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <ImageIcon className="h-5 w-5 text-slate-300" data-testid={`${testId}-empty`} />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button" variant="outline" size="sm" disabled={uploading}
                onClick={() => fileRef.current?.click()}
                data-testid={`${testId}-upload-btn`}
              >
                {uploading
                  ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  : <Upload className="mr-2 h-4 w-4" />}
                {value ? 'Replace' : 'Upload'}
              </Button>
              <Button
                type="button" variant="outline" size="sm" disabled={uploading}
                onClick={() => setLibraryOpen(true)}
                data-testid={`${testId}-library-btn`}
              >
                <Library className="mr-2 h-4 w-4" />
                Choose existing
              </Button>
              {value && (
                <Button
                  type="button" variant="ghost" size="sm"
                  className="text-rose-600 hover:bg-rose-50"
                  onClick={() => onChange('')}
                  data-testid={`${testId}-remove-btn`}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Remove
                </Button>
              )}
            </div>
            <p className="mt-1.5 text-[11px] text-slate-500">
              {hint || 'Drag an image here, or upload from your device. JPG, PNG or WebP, up to 5 MB.'}
            </p>
            {value && (
              <p className="mt-1 truncate text-[10px] text-slate-400" data-testid={`${testId}-current-url`}>
                {value}
              </p>
            )}
          </div>
        </div>

        {onAltTextChange && (
          <div className="mt-3">
            <Label className="text-[11px]">Alt text — describes the image for screen readers</Label>
            <Input
              value={altText || ''}
              onChange={(e) => onAltTextChange(e.target.value)}
              placeholder="e.g. Clinic coordinator reviewing a patient treatment plan"
              className="h-8 text-sm"
              data-testid={`${testId}-alt-text`}
            />
          </div>
        )}

        {onCaptionChange && (
          <div className="mt-2">
            <Label className="text-[11px]">Caption — shown under the image on the page (optional)</Label>
            <Input
              value={caption || ''}
              onChange={(e) => onCaptionChange(e.target.value)}
              placeholder="Leave empty for no caption"
              className="h-8 text-sm"
              data-testid={`${testId}-caption`}
            />
          </div>
        )}

        <div className="mt-2">
          {showLink ? (
            <div className="flex items-center gap-2">
              <Input
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                placeholder="https://…"
                className="h-8 text-sm"
                data-testid={`${testId}-url-input`}
              />
              <Button type="button" variant="ghost" size="sm" onClick={() => setShowLink(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowLink(true)}
              className="inline-flex items-center gap-1 text-[11px] text-slate-400 underline-offset-2 hover:text-slate-600 hover:underline"
              data-testid={`${testId}-use-link`}
            >
              <Link2 className="h-3 w-3" />
              Use a link instead
            </button>
          )}
        </div>
      </div>

      <LibraryModal
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onSelect={onChange}
        testId={testId}
      />
    </div>
  );
};

export default MediaPicker;
